import type { TMeMembership, TNotificationSummaryOrganization } from 'types'

/** 購読する個人UUIDチャネルを求める。サマリー未取得の間は `/me` の ACTIVE 所属を使う（設計書 §9.8）。 */
export function resolvePersonalChannels(
  memberships: readonly TMeMembership[] | undefined,
  summaryOrganizations:
    | readonly TNotificationSummaryOrganization[]
    | undefined,
): string[] {
  const memberUuidByOrganizationId = new Map<number, string>()
  for (const membership of memberships ?? []) {
    if (!membership) continue
    const memberUuid = membership.memberUuid
    if (typeof memberUuid !== 'string' || memberUuid.length === 0) continue
    memberUuidByOrganizationId.set(membership.organizationId, memberUuid)
  }

  const organizationIds = summaryOrganizations
    ? summaryOrganizations.map((organization) => organization?.organizationId)
    : Array.from(memberUuidByOrganizationId.keys())

  const channels: string[] = []
  const seen = new Set<string>()
  for (const organizationId of organizationIds) {
    const memberUuid = memberUuidByOrganizationId.get(organizationId)
    if (!memberUuid || seen.has(memberUuid)) continue
    seen.add(memberUuid)
    channels.push(memberUuid)
  }
  return channels
}

/** 購読中と次の購読対象の差分（増えた分だけ購読し、消えた組織だけ解除する） */
export function diffPersonalChannels(
  subscribed: readonly string[],
  next: readonly string[],
): { added: string[]; removed: string[] } {
  const subscribedChannels = new Set(subscribed)
  const nextChannels = new Set(next)
  return {
    added: next.filter((channel) => !subscribedChannels.has(channel)),
    removed: subscribed.filter((channel) => !nextChannels.has(channel)),
  }
}

/** 個人UUIDチャネル名（所属の memberUuid）から組織を求める。所属に無いチャネルは null。 */
export function findPersonalChannelOrganizationId(
  memberships: readonly TMeMembership[] | undefined,
  channel: string,
): number | null {
  const membership = memberships?.find(
    (candidate) => candidate?.memberUuid === channel,
  )
  return membership ? membership.organizationId : null
}

/** 別組織の所属の個人チャネルに届いたイベントか。所属に無いチャネル（INQUIRY など）は別組織と見なさない。 */
export function isOtherOrganizationPersonalChannel(
  memberships: readonly TMeMembership[] | undefined,
  channel: string,
  organizationId: number | null,
): boolean {
  const channelOrganizationId = findPersonalChannelOrganizationId(
    memberships,
    channel,
  )
  return channelOrganizationId !== null && channelOrganizationId !== organizationId
}
